import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Collapse,
  Divider,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { toast } from "react-toastify";
import { userSidemenuPreferenceService } from "../../utils/services/user-sidemenu-preference.service";
import { useDarkMode } from "../context/darkModeContext";

// ─── Insertion line shown between drag items ─────────────────────
const InsertionLine = () => (
  <Box
    sx={{
      height: "3px",
      borderRadius: "2px",
      backgroundColor: "primary.main",
      mx: 0.5,
      mb: 0.5,
      "@keyframes insertPulse": {
        "0%": { opacity: 0.6, transform: "scaleX(0.95)" },
        "50%": { opacity: 1, transform: "scaleX(1)" },
        "100%": { opacity: 0.6, transform: "scaleX(0.95)" },
      },
      animation: "insertPulse 0.7s ease-in-out infinite",
    }}
  />
);

// ─── Mini sidebar preview item ───────────────────────────────────
const PreviewItem = ({ item, depth = 0 }) => {
  const [open, setOpen] = useState(true);
  return (
    <Box>
      <Box
        onClick={() => item.subNav?.length && setOpen((p) => !p)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.5,
          py: 0.6,
          pl: depth === 0 ? 1.5 : 3.5,
          cursor: item.subNav?.length ? "pointer" : "default",
          borderRadius: "6px",
          mx: 0.5,
          transition: "background 0.12s",
          "&:hover": { backgroundColor: "var(--hover-bg)" },
        }}
      >
        <Box
          sx={{
            width: depth === 0 ? 6 : 4,
            height: depth === 0 ? 6 : 4,
            borderRadius: "50%",
            backgroundColor: depth === 0 ? "primary.main" : "var(--text-secondary)",
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            flex: 1,
            fontFamily: "poppins",
            fontSize: depth === 0 ? "11.5px" : "10.5px",
            fontWeight: depth === 0 ? 600 : 400,
            color: depth === 0 ? "var(--text-primary)" : "var(--text-secondary)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.title}
        </Typography>
        {item.subNav?.length > 0 &&
          (open ? (
            <ExpandMoreIcon sx={{ fontSize: 13, color: "var(--text-secondary)" }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 13, color: "var(--text-secondary)" }} />
          ))}
      </Box>
      {item.subNav?.length > 0 && (
        <Collapse in={open}>
          <Box
            sx={{
              borderLeft: "1.5px solid var(--border-color)",
              ml: 2.5,
              mt: 0.25,
              mb: 0.25,
            }}
          >
            {item.subNav.map((child) => (
              <PreviewItem key={child.pkey} item={child} depth={1} />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

// ─── Main modal component ────────────────────────────────────────
const SidemenuPersonalisationModal = ({ open, onClose }) => {
  const { darkMode } = useDarkMode();
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});

  // drag state
  const [draggingParent, setDraggingParent] = useState(null);
  const [overParent, setOverParent] = useState(null);
  const [draggingChild, setDraggingChild] = useState(null);
  const [overChild, setOverChild] = useState(null);

  const [saving, setSaving] = useState(false);

  const [renamingKey, setRenamingKey] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const startRename = (e, pkey, currentTitle) => {
    e.stopPropagation();
    e.preventDefault();
    setRenamingKey(pkey);
    setRenameValue(currentTitle);
  };

  const confirmRename = async (pkey) => {
    const trimmed = renameValue.trim();
    setRenamingKey(null);
    setRenameValue("");
    if (!trimmed) return;

    const updatedItems = items.map((item) => {
      if (item.pkey === pkey) return { ...item, title: trimmed };
      return {
        ...item,
        subNav: item.subNav?.map((child) =>
          child.pkey === pkey ? { ...child, title: trimmed } : child
        ) ?? null,
      };
    });
    setItems(updatedItems);

    const userId = (() => {
      try {
        return JSON.parse(localStorage.getItem("user"))?._id;
      } catch {
        return null;
      }
    })();
    if (!userId) return;

    const preferences = [];
    updatedItems.forEach((item, idx) => {
      preferences.push({ pkey: item.pkey, order: idx, label: item.title });
      item.subNav?.forEach((child, childIdx) => {
        preferences.push({ pkey: child.pkey, order: childIdx, label: child.title });
      });
    });

    try {
      await userSidemenuPreferenceService.upsert(userId, preferences);
      localStorage.setItem("sidemenuItems", JSON.stringify(updatedItems));
      window.dispatchEvent(new Event("sidemenu-updated"));
    } catch (e) {
      console.error(e);
    }
  };

  const cancelRename = () => {
    setRenamingKey(null);
    setRenameValue("");
  };

  useEffect(() => {
    if (open) {
      const raw = localStorage.getItem("sidemenuItems");
      const loaded = raw
        ? JSON.parse(raw).map((item) => ({
            ...item,
            subNav: item.subNav ? [...item.subNav] : null,
          }))
        : [];
      setItems(loaded);
      const exp = {};
      loaded.forEach((item) => {
        if (item.subNav?.length) exp[item.pkey] = true;
      });
      setExpanded(exp);
    }
  }, [open]);

  const handleClose = () => {
    setDraggingParent(null);
    setOverParent(null);
    setDraggingChild(null);
    setOverChild(null);
    setRenamingKey(null);
    setRenameValue("");
    onClose();
  };

  const toggleExpanded = (pkey) =>
    setExpanded((prev) => ({ ...prev, [pkey]: !prev[pkey] }));

  // ── Parent drag ──────────────────────────────────────────────────
  const onParentDragStart = (e, idx) => {
    setDraggingParent(idx);
    setDraggingChild(null);
    e.dataTransfer.effectAllowed = "move";
  };

  const onParentDragOver = (e, idx) => {
    e.preventDefault();
    if (draggingChild !== null) return;
    if (overParent !== idx) setOverParent(idx);
  };

  const onParentDrop = (e, idx) => {
    e.preventDefault();
    const from = draggingParent;
    if (from === null || from === idx) {
      setDraggingParent(null);
      setOverParent(null);
      return;
    }
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDraggingParent(null);
    setOverParent(null);
  };

  const onParentDragEnd = () => {
    setDraggingParent(null);
    setOverParent(null);
  };

  // ── Child drag ───────────────────────────────────────────────────
  const onChildDragStart = (e, parentIdx, childIdx) => {
    e.stopPropagation();
    setDraggingChild({ parentIdx, childIdx });
    setDraggingParent(null);
    e.dataTransfer.effectAllowed = "move";
  };

  const onChildDragOver = (e, parentIdx, childIdx) => {
    e.preventDefault();
    e.stopPropagation();
    setOverChild({ parentIdx, childIdx });
  };

  const onChildDrop = (e, parentIdx, childIdx) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      !draggingChild ||
      draggingChild.parentIdx !== parentIdx ||
      draggingChild.childIdx === childIdx
    ) {
      setDraggingChild(null);
      setOverChild(null);
      return;
    }
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== parentIdx) return item;
        const children = [...(item.subNav || [])];
        const [moved] = children.splice(draggingChild.childIdx, 1);
        children.splice(childIdx, 0, moved);
        return { ...item, subNav: children };
      }),
    );
    setDraggingChild(null);
    setOverChild(null);
  };

  const onChildDragEnd = () => {
    setDraggingChild(null);
    setOverChild(null);
  };

  const handleSave = async () => {
    const userId = (() => {
      try {
        return JSON.parse(localStorage.getItem("user"))?._id;
      } catch {
        return null;
      }
    })();
    if (!userId) return toast.error("User not found");

    const preferences = [];
    items.forEach((item, idx) => {
      preferences.push({ pkey: item.pkey, order: idx, label: item.title });
      item.subNav?.forEach((child, childIdx) => {
        preferences.push({ pkey: child.pkey, order: childIdx, label: child.title });
      });
    });

    setSaving(true);
    try {
      await userSidemenuPreferenceService.upsert(userId, preferences);
      localStorage.setItem("sidemenuItems", JSON.stringify(items));
      window.dispatchEvent(new Event("sidemenu-updated"));
      toast.success("Menu preferences saved");
      handleClose();
    } catch (e) {
      console.error(e);
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const isParentDragging = (idx) => draggingParent === idx;
  // Show insertion line BEFORE the overParent index (when something is being dragged)
  const showInsertBefore = (idx) =>
    draggingParent !== null && overParent === idx && draggingParent !== idx;
  const isChildDragging = (pi, ci) =>
    draggingChild?.parentIdx === pi && draggingChild?.childIdx === ci;
  const showChildInsertBefore = (pi, ci) =>
    draggingChild !== null &&
    overChild?.parentIdx === pi &&
    overChild?.childIdx === ci &&
    draggingChild?.childIdx !== ci;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "var(--bg-paper)",
          boxShadow: darkMode
            ? "0 24px 64px rgba(0,0,0,0.6)"
            : "0 24px 64px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: (theme) =>
            darkMode
              ? "linear-gradient(135deg, #1e3a5f 0%, #2d1b69 100%)"
              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || theme.palette.primary.main} 100%)`,
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              backgroundColor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ViewSidebarOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: "poppins",
                fontWeight: 700,
                fontSize: "15px",
                color: "#fff",
                letterSpacing: "0.3px",
              }}
            >
              Personalise Your Menu
            </Typography>
            <Typography
              sx={{
                fontFamily: "poppins",
                fontSize: "11px",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              Drag to reorder · click ✎ to rename
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon sx={{ color: "rgba(255,255,255,0.85)", fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* ── Body ── */}
      <DialogContent sx={{ p: 0, display: "flex", height: "520px" }}>

        {/* LEFT: drag-and-drop editor */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2.5,
            pt: 2.5,
            pb: 1,
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 8,
              backgroundColor: "var(--border-color)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Typography
              sx={{
                fontFamily: "poppins",
                fontSize: "11px",
                fontWeight: 600,
                color: "var(--text-secondary)",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
              }}
            >
              Reorder Navigation
            </Typography>
            {draggingParent !== null && (
              <Typography
                sx={{
                  fontFamily: "poppins",
                  fontSize: "10px",
                  color: "primary.main",
                  fontWeight: 600,
                  "@keyframes fadeInOut": {
                    "0%": { opacity: 0.5 },
                    "50%": { opacity: 1 },
                    "100%": { opacity: 0.5 },
                  },
                  animation: "fadeInOut 1s ease-in-out infinite",
                }}
              >
                Drop to place here ↓
              </Typography>
            )}
          </Box>

          {items.map((item, parentIdx) => (
            <Box key={item.pkey}>
              {/* ── insertion line before this item ── */}
              {showInsertBefore(parentIdx) && <InsertionLine />}

              <Box
                draggable
                onDragStart={(e) => onParentDragStart(e, parentIdx)}
                onDragOver={(e) => onParentDragOver(e, parentIdx)}
                onDragLeave={() => setOverParent(null)}
                onDrop={(e) => onParentDrop(e, parentIdx)}
                onDragEnd={onParentDragEnd}
                sx={{
                  mb: 1,
                  borderRadius: "10px",
                  border: "1.5px solid",
                  borderColor: isParentDragging(parentIdx)
                    ? "primary.main"
                    : "var(--border-color)",
                  backgroundColor: isParentDragging(parentIdx)
                    ? "var(--bg-highlight)"
                    : "var(--bg-secondary)",
                  // being-dragged: show as ghost placeholder
                  opacity: isParentDragging(parentIdx) ? 0.35 : 1,
                  // style: dashed border when being dragged (source placeholder)
                  borderStyle: isParentDragging(parentIdx) ? "dashed" : "solid",
                  boxShadow: isParentDragging(parentIdx)
                    ? "none"
                    : "0 1px 4px rgba(0,0,0,0.06)",
                  transition: "opacity 0.15s, border-color 0.15s, box-shadow 0.15s",
                  userSelect: "none",
                  cursor: "grab",
                  "&:active": { cursor: "grabbing" },
                }}
              >
                {/* parent header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 1.25,
                    py: 1,
                    gap: 1,
                  }}
                >
                  {/* position number badge */}
                  <Box
                    sx={{
                      minWidth: 22,
                      height: 22,
                      borderRadius: "6px",
                      backgroundColor: isParentDragging(parentIdx)
                        ? "primary.main"
                        : "var(--border-color)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "background-color 0.15s",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "poppins",
                        fontSize: "10px",
                        fontWeight: 700,
                        color: isParentDragging(parentIdx)
                          ? "#fff"
                          : "var(--text-secondary)",
                      }}
                    >
                      {parentIdx + 1}
                    </Typography>
                  </Box>

                  <DragIndicatorIcon
                    sx={{
                      color: "var(--text-secondary)",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  />

                  {renamingKey === item.pkey ? (
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") confirmRename(item.pkey);
                        if (e.key === "Escape") cancelRename();
                        e.stopPropagation();
                      }}
                      onBlur={() => confirmRename(item.pkey)}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        flex: 1,
                        fontFamily: "poppins",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "var(--text-primary)",
                        background: "var(--bg-paper)",
                        border: "1.5px solid var(--border-color)",
                        borderRadius: "6px",
                        padding: "2px 6px",
                        outline: "none",
                        minWidth: 0,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        minWidth: 0,
                        "& .rename-btn": { opacity: 0 },
                        "&:hover .rename-btn": { opacity: 1 },
                      }}
                    >
                      <Typography
                        sx={{
                          flex: 1,
                          fontFamily: "poppins",
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "var(--text-primary)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.title}
                      </Typography>
                      <IconButton
                        className="rename-btn"
                        size="small"
                        onClick={(e) => startRename(e, item.pkey, item.title)}
                        onMouseDown={(e) => e.stopPropagation()}
                        sx={{ p: 0.3, transition: "opacity 0.15s", flexShrink: 0 }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 13, color: "var(--text-secondary)" }} />
                      </IconButton>
                    </Box>
                  )}

                  {item.subNav?.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box
                        sx={{
                          px: 0.75,
                          py: 0.1,
                          borderRadius: "4px",
                          backgroundColor: "var(--border-color)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "poppins",
                            fontSize: "10px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          {item.subNav.length}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => toggleExpanded(item.pkey)}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {expanded[item.pkey] ? (
                          <ExpandLessIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {/* children */}
                {item.subNav?.length > 0 && (
                  <Collapse in={!!expanded[item.pkey]}>
                    <Box
                      sx={{
                        borderTop: "1px dashed var(--border-color)",
                        px: 1.25,
                        pt: 0.5,
                        pb: 0.75,
                      }}
                    >
                      {item.subNav.map((child, childIdx) => (
                        <Box key={child.pkey}>
                          {/* child insertion line */}
                          {showChildInsertBefore(parentIdx, childIdx) && (
                            <Box
                              sx={{
                                height: "2px",
                                borderRadius: "2px",
                                backgroundColor: "primary.main",
                                mx: 1,
                                my: 0.3,
                                "@keyframes insertPulse": {
                                  "0%": { opacity: 0.6 },
                                  "50%": { opacity: 1 },
                                  "100%": { opacity: 0.6 },
                                },
                                animation: "insertPulse 0.7s ease-in-out infinite",
                              }}
                            />
                          )}
                          <Box
                            draggable
                            onDragStart={(e) =>
                              onChildDragStart(e, parentIdx, childIdx)
                            }
                            onDragOver={(e) =>
                              onChildDragOver(e, parentIdx, childIdx)
                            }
                            onDragLeave={(e) => {
                              e.stopPropagation();
                              setOverChild(null);
                            }}
                            onDrop={(e) => onChildDrop(e, parentIdx, childIdx)}
                            onDragEnd={onChildDragEnd}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1,
                              py: 0.75,
                              my: 0.25,
                              borderRadius: "7px",
                              border: "1.5px solid",
                              borderStyle: isChildDragging(parentIdx, childIdx)
                                ? "dashed"
                                : "solid",
                              borderColor: isChildDragging(parentIdx, childIdx)
                                ? "primary.main"
                                : "transparent",
                              backgroundColor: isChildDragging(parentIdx, childIdx)
                                ? "var(--bg-highlight)"
                                : "var(--bg-paper)",
                              opacity: isChildDragging(parentIdx, childIdx) ? 0.35 : 1,
                              cursor: "grab",
                              transition: "all 0.12s ease",
                              "&:active": { cursor: "grabbing" },
                            }}
                          >
                            <Box
                              sx={{
                                width: 3,
                                height: 3,
                                borderRadius: "50%",
                                backgroundColor: "var(--text-secondary)",
                                flexShrink: 0,
                                ml: 0.5,
                              }}
                            />
                            <DragIndicatorIcon
                              sx={{ color: "var(--text-secondary)", fontSize: 14 }}
                            />
                            {renamingKey === child.pkey ? (
                              <input
                                autoFocus
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") confirmRename(child.pkey);
                                  if (e.key === "Escape") cancelRename();
                                  e.stopPropagation();
                                }}
                                onBlur={() => confirmRename(child.pkey)}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                style={{
                                  flex: 1,
                                  fontFamily: "poppins",
                                  fontSize: "11.5px",
                                  color: "var(--text-secondary)",
                                  background: "var(--bg-paper)",
                                  border: "1.5px solid var(--border-color)",
                                  borderRadius: "5px",
                                  padding: "1px 5px",
                                  outline: "none",
                                  minWidth: 0,
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  flex: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  minWidth: 0,
                                  "& .rename-btn": { opacity: 0 },
                                  "&:hover .rename-btn": { opacity: 1 },
                                }}
                              >
                                <Typography
                                  sx={{
                                    flex: 1,
                                    fontFamily: "poppins",
                                    fontSize: "11.5px",
                                    color: "var(--text-secondary)",
                                    fontWeight: 400,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {child.title}
                                </Typography>
                                <IconButton
                                  className="rename-btn"
                                  size="small"
                                  onClick={(e) => startRename(e, child.pkey, child.title)}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  sx={{ p: 0.2, transition: "opacity 0.15s", flexShrink: 0 }}
                                >
                                  <EditOutlinedIcon sx={{ fontSize: 12, color: "var(--text-secondary)" }} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ borderColor: "var(--border-color)" }} />

        {/* RIGHT: live preview */}
        <Box
          sx={{
            width: 220,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "var(--bg-primary)",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: "1px solid var(--border-color)",
            }}
          >
            <Typography
              sx={{
                fontFamily: "poppins",
                fontSize: "11px",
                fontWeight: 600,
                color: "var(--text-secondary)",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
              }}
            >
              Live Preview
            </Typography>
          </Box>

          {/* mini sidebar */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              backgroundColor: "var(--bg-paper)",
              mx: 1.5,
              my: 1.5,
              borderRadius: "10px",
              boxShadow: darkMode
                ? "0 2px 12px rgba(0,0,0,0.4)"
                : "0 2px 12px rgba(0,0,0,0.1)",
              border: "1px solid var(--border-color)",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {/* logo placeholder */}
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: "1px solid var(--border-color)",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "6px",
                  backgroundColor: "primary.main",
                  flexShrink: 0,
                }}
              />
              <Box>
                <Box sx={{ width: 60, height: 6, borderRadius: 3, backgroundColor: "var(--text-primary)", opacity: 0.4 }} />
                <Box sx={{ width: 40, height: 4, borderRadius: 3, backgroundColor: "var(--text-secondary)", opacity: 0.25, mt: 0.5 }} />
              </Box>
            </Box>

            {/* nav items */}
            <Box sx={{ py: 1 }}>
              {items.map((item) => (
                <PreviewItem key={item.pkey} item={item} />
              ))}
            </Box>
          </Box>

          <Typography
            sx={{
              fontFamily: "poppins",
              fontSize: "10px",
              color: "var(--text-secondary)",
              textAlign: "center",
              pb: 1.5,
              opacity: 0.7,
            }}
          >
            Updates as you drag
          </Typography>
        </Box>
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid var(--border-color)",
          backgroundColor: "var(--bg-paper)",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            flex: 1,
            fontFamily: "poppins",
            fontSize: "11px",
            color: "var(--text-secondary)",
          }}
        >
          Changes apply to your account only.
        </Typography>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            fontFamily: "poppins",
            fontSize: "12px",
            borderColor: "var(--border-color)",
            color: "var(--text-primary)",
            "&:hover": { borderColor: "var(--text-secondary)" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            fontFamily: "poppins",
            fontSize: "12px",
            background: (theme) =>
              darkMode
                ? "linear-gradient(135deg, #0040FF 0%, #6366f1 100%)"
                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || theme.palette.primary.main} 100%)`,
            px: 2.5,
            "&:hover": { opacity: 0.9 },
          }}
        >
          {saving ? "Saving…" : "Save Preferences"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SidemenuPersonalisationModal;
